import { useDispatch, useSelector } from "react-redux";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect } from "react";

import { Button, Switch } from "@chef/components";
import {
  selectSchedulerModal,
  setWeekSchedulerEditorModal,
  useCreateOrUpdateSchedulerMutation,
} from "@chef/state-management";
import { useCalendarData } from "@chef/state-management/hooks";
import { useExtendedForm, useIntercom } from "@chef/hooks";
import { getCurrentWeekDataAndBasketFromCalendar } from "@chef/state-management/helpers";
import { formatDate } from "@chef/helpers";
import { isDeepEquals } from "@chef/utils/equal";

import { TimeblocksCheckboxRadio } from "./TimeblocksCheckboxRadio";
import { AddressDropdown } from "./AddressDropdown";
import type { WeekSchedulerFormData } from "./types";
import { Modal } from "../modal/Modal";

import { intl } from "./WeekSchedulerEditor.Intl";

const schema = yup.object().shape({
  pauseWeek: yup.boolean().required(),
  addressId: yup.string().required(),
  timeblockId: yup.number().nullable(),
});

const RANGE = 12;

export const WeekSchedulerEditor = () => {
  const { hideBubble, showBubble } = useIntercom();

  const dispatch = useDispatch();
  const { open, week, year } = useSelector(selectSchedulerModal);

  const [createOrUpdateScheduler] = useCreateOrUpdateSchedulerMutation();

  const { calendar } = useCalendarData({
    week,
    year,
    range: RANGE,
    skip: !open,
  });

  const calendarWeek = calendar
    ? getCurrentWeekDataAndBasketFromCalendar({
        calendar,
        week,
        year,
      })?.mealboxBasket
    : null;

  const hasDelivery = calendarWeek?.shippingDetails.hasDelivery;

  const currentAddressId = calendarWeek?.shippingDetails.addressInfo.addressId;

  const plannedDeliveryInformation =
    calendarWeek?.shippingDetails.plannedDeliveryInformation;

  const currentCutoff = plannedDeliveryInformation?.cutOffDateTime;
  const currentTimeblockId = plannedDeliveryInformation?.actualTimeblockId;

  const formControls = useExtendedForm<WeekSchedulerFormData>({
    resolver: yupResolver(schema),
  });

  const { setValue, formState, handleSubmit, getValues, formValues } =
    formControls;

  const isDirty = !isDeepEquals(formValues, {
    addressId: currentAddressId,
    timeblockId: currentTimeblockId,
    pauseWeek: !hasDelivery,
  });

  const handleClose = () => {
    dispatch(setWeekSchedulerEditorModal({ open: false }));
  };

  useEffect(() => {
    if (open) {
      hideBubble();
    } else {
      showBubble();
    }
  }, [hideBubble, showBubble, open]);

  useEffect(() => {
    if (!currentAddressId) {
      return;
    }

    setValue("addressId", currentAddressId);
  }, [currentAddressId, setValue]);

  useEffect(() => {
    if (!currentTimeblockId) {
      return;
    }

    setValue("timeblockId", currentTimeblockId);
  }, [currentTimeblockId, setValue]);

  useEffect(() => {
    if (hasDelivery === undefined) {
      return;
    }

    setValue("pauseWeek", !hasDelivery);
  }, [hasDelivery, setValue]);

  useEffect(() => {
    return () => {
      handleClose();
    };
  }, []);

  const onSubmit = async (data: WeekSchedulerFormData) => {
    if (data.timeblockId === null) {
      return;
    }

    await createOrUpdateScheduler({
      week,
      year,
      addressId: data.addressId,
      timeblockId: data.timeblockId,
      delivery: !data.pauseWeek,
    }).unwrap();

    handleClose();
  };

  return (
    <Modal
      size="sm"
      panel="card"
      open={open}
      onClose={handleClose}
      interactions={[
        ["top-close-button", "enabled"],
        ["pull-down", "mobile-only"],
      ]}
      animation="fly-up"
    >
      <h1 className="mt-2 text-xl">
        <strong>
          {intl.MANAGE_DELIVERY_WEEK} {week}
        </strong>
      </h1>

      <p className="mt-2 text-sm text-grey-1">
        {intl.DEADLINE_FOR_DELIVERY_CHANGE}{" "}
        <strong>
          {currentCutoff &&
            formatDate(
              new Date(currentCutoff),
              `EEEE d. MMMM '${intl.TIME_MARKER}' HH:mm`,
            ).toLocaleLowerCase()}
        </strong>
      </p>

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col gap-8 mt-4"
      >
        <div className="flex items-center justify-between">
          <strong className="text-sm">{intl.PAUSE_WEEK}</strong>
          <Switch
            label={intl.PAUSE_WEEK}
            checked={getValues("pauseWeek")}
            onChange={(checked) => setValue("pauseWeek", checked)}
          />
        </div>

        <div>
          <AddressDropdown
            formControls={formControls}
            handleModalClose={handleClose}
          />
        </div>

        <div>
          <TimeblocksCheckboxRadio
            formControls={formControls}
            week={week}
            year={year}
            calendarTimeblockId={currentTimeblockId}
          />
        </div>

        <div className="flex gap-3">
          <Button
            type="button"
            onClick={handleClose}
            outlined
            tiny
            className="grow"
          >
            {intl.CANCEL}
          </Button>
          {isDirty && (
            <Button
              type="submit"
              loading={formState.isSubmitting}
              disabled={formControls.getValues("timeblockId") === null}
              primary
              tiny
              className="grow"
            >
              {intl.SAVE_CHANGES}
            </Button>
          )}
        </div>
      </form>
    </Modal>
  );
};
